import { useEffect, useId, useState } from 'react';

import { cn } from '@/lib/utils';

import { ChevronDownIcon } from '@icons/ChevronDownIcon';

import {
  isExpandableActive,
  isExpandableInitiallyExpanded,
  isLinkActive,
  resolveHref,
} from '../Sidebar/active';
import { ICON_MAP } from '../Sidebar/iconMap';
import type { ExpandableChild, ExpandableItem } from '../Sidebar/types';
import {
  ACTIVE_ROW_CLASS,
  CHILD_LIST_CLASS,
  CHILD_ROW_CLASS,
  DISABLED_ITEM_CLASS,
  HOVER_ROW_CLASS,
  ITEM_ROW_CLASS,
  NAV_ICON_CLASS,
} from './constants';

interface MobileSidebarExpandableProps {
  item: ExpandableItem;
  pathname: string;
  defaultExpanded: boolean;
  onClose: () => void;
}

function childHref(child: ExpandableChild): string {
  return resolveHref(child);
}

function ChildLink({
  child,
  pathname,
  onClose,
}: {
  child: ExpandableChild;
  pathname: string;
  onClose: () => void;
}) {
  const active = isLinkActive(child, pathname);
  return (
    <a
      href={childHref(child)}
      data-nav-active={active || undefined}
      data-turbo-frame="_top"
      onClick={onClose}
      className={cn(CHILD_ROW_CLASS, active && ACTIVE_ROW_CLASS)}
    >
      {child.label}
    </a>
  );
}

// Each expandable owns its expansion state — mirrors desktop SidebarExpandable
// URL-driven initial expansion with manual toggles persisting until navigation.
export function MobileSidebarExpandable({
  item,
  pathname,
  defaultExpanded,
  onClose,
}: MobileSidebarExpandableProps) {
  const panelId = useId();
  const active = isExpandableActive(item, pathname);
  const [expanded, setExpanded] = useState(
    defaultExpanded || isExpandableInitiallyExpanded(item, pathname),
  );
  const Icon = ICON_MAP[item.icon];

  useEffect(() => {
    setExpanded(defaultExpanded || isExpandableInitiallyExpanded(item, pathname));
  }, [item, pathname, defaultExpanded]);

  return (
    <div>
      <button
        type="button"
        className={cn(ITEM_ROW_CLASS, HOVER_ROW_CLASS, 'justify-between', active && ACTIVE_ROW_CLASS)}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="flex items-center gap-4">
          <Icon className={NAV_ICON_CLASS} aria-hidden="true" />
          {item.label}
        </span>
        <ChevronDownIcon
          className={cn(
            'size-6 chevron-motion',
            expanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <ul id={panelId} className={CHILD_LIST_CLASS}>
          {item.children.map((child) => (
            <li key={child.label}>
              {child.disabled ? (
                <div className={cn(CHILD_ROW_CLASS, DISABLED_ITEM_CLASS)}>{child.label}</div>
              ) : (
                <ChildLink child={child} pathname={pathname} onClose={onClose} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
